/**
 * /guides/wedding-planning-spreadsheet-what-to-track
 *
 * "Wedding Planning Spreadsheet: What Should You Track?" — the fourteenth
 * guide, and the one that names the system the ninth, tenth and eleventh each
 * describe one part of. The checklist owns WHEN the work happens, the vendor
 * guide owns each commitment as its own record, the budget guide owns the
 * money; this one answers what a couple should be holding at all, and in what
 * shape.
 *
 * ── The differentiator, which is the whole piece ──────────────────────────
 * The SERP for this query is almost entirely template distribution: free
 * downloads, "the N tabs you need", paid sheets, and a handful of
 * spreadsheet-tutorial posts. Every one of them answers the question by
 * enumerating tabs, and they enumerate roughly the same five — budget, guests,
 * vendors, timeline, seating — with columns underneath. None of them says what
 * a tracker is FOR, so none can tell a reader which column to leave out.
 *
 * The article's answer is the opposite of a template's: a good tracker is not
 * the one that tracks everything, it is the one that keeps visible what needs
 * attention, who owns it, what has been decided, and what remains. Stated in
 * the intro and the first section, and then applied as the test in every later
 * section — "a section earns its place when it solves a planning problem you
 * actually have".
 *
 * The organising idea is the three-way split in "Information, Decisions and
 * Actions". It is offered as a practical way to sort things and explicitly NOT
 * as an established framework — a `note` block says so, in the section, because
 * dressing an editorial device up as industry methodology is exactly the kind
 * of unearned authority the content principles rule out. Its payoff is that it
 * explains the two areas the SERP consistently omits: decisions are a category
 * of thing a plan holds rather than a variant of a task, and documents are
 * information the tracker should point at rather than contain.
 *
 * Two sections carry the piece and must not be softened into tips:
 *   - "Track Decisions and Open Questions", which is the strongest thing here
 *     and the one no competing article has.
 *   - "Track Payments Separately From the Budget", which reuses the budget
 *     guide's committed/paid distinction in one paragraph rather than
 *     re-teaching it.
 *
 * ── Structure, and the four merges ────────────────────────────────────────
 * Fourteen sections against a commission asking for thirteen to sixteen, and a
 * suggested eighteen headings. Four pairs were MERGED, each because the pair
 * was one subject written twice, and no subject was dropped:
 *
 *   1. "Give Every Task a Deadline and Owner" folded into "Track Tasks, Not
 *      Just Categories" as its second H3. Both answer one question — what makes
 *      a row on a task list useful — and the fields only make sense once the
 *      category/action distinction has been drawn. The mandated heading
 *      survives verbatim as the subheading.
 *   2. "When a Spreadsheet Starts Getting in the Way" folded into "Spreadsheet
 *      or Wedding Planning Tool?" as its first H3. The commission itself says
 *      the first section exists to introduce the distinction the second then
 *      draws; that is one argument, and split across two H2s it states its
 *      premise twice.
 *   3. "Keep the Spreadsheet Simple Enough to Maintain" and "How Often Should
 *      You Update It?" became "Keeping It Simple, and Keeping It Current", two
 *      H3s under one heading. Both are about the cost of keeping a tracker
 *      true; one is the cost per column, the other the cost per week. The
 *      question survives as an H3 and as an FAQ.
 *   4. "Add Seating and Other Detailed Areas Only When You Need Them" folded
 *      into "What a Simple Wedding Planning Spreadsheet Looks Like" as its
 *      closing two paragraphs. The principle is the caveat on the eight-area
 *      sketch — give the list, then say not to build all of it on the first
 *      afternoon — and reads better attached to the list than as an H2 arguing
 *      with the one above it.
 *
 * The "table" the commission sketches (Area / What to track / Useful fields) is
 * the eight-item list in "What a Simple Wedding Planning Spreadsheet Looks
 * Like", each item carrying all three columns on one line. The block vocabulary
 * in `guideModel.js` has no table type, and adding one would mean changing both
 * renderers, the generator and the test — the ninth, tenth and eleventh guides
 * each resolved the same brief the same way.
 *
 * ── Length ────────────────────────────────────────────────────────────────
 * About 3,170 rendered words before the FAQ, against a commissioned range of
 * roughly 2,500-2,900, and reported as such rather than quietly absorbed. Four
 * compression passes took it down from 4,188 — the four merges above, the
 * removal of a genuine duplication (the eight areas were listed twice, once in
 * the first section and once in the field table; the first is now prose), and
 * the deletion of every paragraph that restated the one above it.
 *
 * What is left is not padding. This commission carries eight working areas each
 * with its own field list, a three-part framework with three H3s, a two-sided
 * comparison with three H3s and three lists, the mandated eight-row table, and
 * a closing section with eight verified product claims and six denials. The
 * remaining excess is roughly one paragraph per section, and taking it would
 * mean dropping a field list, a hedge or a claim rather than tightening one.
 * The budget guide made the same call at 2,847 and wrote it down; this is the
 * same call, larger, and the same disclosure. Anything added here should
 * replace something rather than extend it.
 *
 * ── The hedges, which are load-bearing ────────────────────────────────────
 *   - No universal status vocabulary. The commission asks for this explicitly,
 *     and the article says the words are the reader's to choose and that
 *     consistency is the only requirement.
 *   - No prescribed update schedule. Weekly appears once, as something many
 *     couples find comfortable once vendors are booked, immediately beside
 *     monthly and followed by "the right rhythm is the one you keep".
 *   - No claim that a planning tool is better. The comparison is symmetrical,
 *     both lists are real, and "plenty of couples end up with both" is named as
 *     a legitimate answer rather than indecision.
 *   - No vendor timing asserted as convention. The timeline section says the
 *     dates are given to you by contracts and vendors, and never names one.
 *   - No spreadsheet mechanics. This is not an Excel tutorial: no formulas, no
 *     conditional formatting, no comparison between Sheets and Excel.
 *
 * ── The claims this guide is allowed to make ──────────────────────────────
 * All in the closing section, all verified against the shipped code:
 *
 *   - "Agapaé's Planning Hub and Éros are part of the Heirloom plan."
 *     `api/constants/planFeatures.mjs`: both hang off `invitationProduct`,
 *     which is `pro` (Heirloom) only, enforced at the router in
 *     `api/routes/organizer.routes.mjs` via `requireProPlanWithCode`.
 *   - planning divided into editable categories, each holding its own vendors,
 *     tasks, costs, documents and decisions  (`api/constants/organizerSeeds.mjs`
 *     seeds venue, catering, photography, videography, flowers … ;
 *     PlanningCategorySchema carries `isCustom` for user-created ones)
 *   - a vendor card with contact person, details, notes and a status that
 *     follows the conversation  (ORGANIZER_VENDOR_STATUSES: researching,
 *     contacted, awaiting_quote, quoted, booked, completed, passed)
 *   - a task carries a due date, priority, labels and its own checklist, and is
 *     flagged past its date  (OrganizerTaskSchema: status todo/in_progress/done,
 *     priority, labels, subtasks, dueDate; `overdue_tasks` in
 *     `api/utils/organizerCompute.mjs`)
 *   - a cost carries a label, amount, due date, category and vendor, is due or
 *     paid, and rolls up to estimated cost / paid / outstanding / budget
 *     remaining  (OrganizerPaymentSchema; `computeOverallBudget`; the labels are
 *     `organizer.budget.*` and render on `app/.../organizer/BudgetPage.tsx`)
 *   - decisions with the options under consideration and a recorded outcome
 *     (OrganizerDecisionSchema: title, options, status open/decided, outcome)
 *   - contracts, quotes and receipts filed as PDFs or photographs against the
 *     vendor or task they belong to  (OrganizerDocumentSchema;
 *     DOCUMENT_MIME_TYPES in organizer.routes.mjs is PDF + images ONLY, which is
 *     why the copy names the two formats rather than saying "files")
 *   - dated tasks, unpaid dated payments and meetings on one calendar
 *     (`GET /:eventId/organizer/calendar`)
 *   - milestones on a timeline  (OrganizerMilestoneSchema, TimelinePage.tsx)
 *   - Éros reads the plan and makes a change when asked  (`get_planning_overview`,
 *     `get_budget_breakdown`, `list_tasks`, `list_vendors`; `create_task`,
 *     `update_task`, `create_vendor`, `update_vendor`, `create_payment`,
 *     `update_payment`, `create_decision`, `update_decision` in
 *     `api/services/assistantTools.mjs`)
 *
 * TWO THINGS ARE DELIBERATELY NOT CLAIMED, having been checked and found false:
 *
 *   - Task OWNERSHIP. The article recommends an owner column, and Agapaé has no
 *     assignee field on OrganizerTaskSchema — there is no per-task owner in the
 *     model. The closing section therefore lists what a task does carry and
 *     never says the product assigns one. Do not "complete" that sentence.
 *   - Spreadsheet IMPORT or SYNC. There is no CSV/XLSX import anywhere: guests
 *     have an EXPORT only (`routes/guests.routes.mjs` writes text/csv), and the
 *     organizer document allowlist is PDF and images. Both are denied outright,
 *     because on this article of all articles a reader will assume otherwise.
 *
 * And the standing boundaries, each a real product limit: not a vendor
 * directory, nothing contacted, booked or paid through it; no reminders about
 * vendor payments (the only scheduled mail in `api/services/` is the gallery
 * retention reminder, and the Stripe mails are about the couple's own
 * subscription — which is why that denial stays scoped to VENDOR payments); not
 * accounting software; no substitute for signed agreements; and Éros suggests
 * while the couple decides.
 *
 * ── Boundaries with the guides it links to ────────────────────────────────
 * This guide is the map and never the territory: each area gets the shape of
 * the record and hands off. The guest-list guide owns building and grouping a
 * list; the vendor guide owns the contract and the payment schedule; the budget
 * guide owns the money; the checklist owns the order. Five internal
 * destinations, each linked exactly twice (once in prose at the point the
 * subject changes, once in the related block or as the product page), which is
 * inside both the commission's preference and the cap the test enforces.
 *
 * The seating guides are deliberately NOT linked. Seating appears as the worked
 * example of an area that earns its place late, and the whole point of that
 * passage is that it is not yet the reader's problem; sending them to a full
 * seating methodology mid-sentence argues against the paragraph it sits in.
 *
 * ── Hero ──────────────────────────────────────────────────────────────────
 * All thirteen images the earlier guides used are used exactly once each, so
 * this one reaches into `public/assets/photos/` — the frame is already on the
 * site (the /wedding-planning hero) but has never been a guide hero, and it is
 * the only unused landscape photograph on the domain.
 *
 * No copy in this file may contain "&", "<" or ">". The generator escapes them
 * correctly, but `guides.seo.test.mjs` compares against tag-stripped HTML
 * without decoding entities, so a string containing one is reported missing
 * from a document that in fact carries it.
 */
const weddingPlanningSpreadsheetWhatToTrackEn = {
  slug: "wedding-planning-spreadsheet-what-to-track",

  title: "Wedding Planning Spreadsheet: What Should You Track?",
  metaTitle: "Wedding Planning Spreadsheet: What Should You Track? | Agapaé",
  metaDescription:
    "Learn what to track in a wedding planning spreadsheet, from tasks and deadlines to guests, vendors, payments, budget, decisions, and the wedding-day timeline.",

  eyebrow: "Wedding planning",

  hero: {
    src: "/assets/photos/venice_luxury_wedding_photographer_1_von_1.jpg",
    // Describes the photograph, not the keyword: a black and white frame of
    // long wooden tables laid for dinner under trees at dusk, tapered candles
    // burning the length of them, glassware and low arrangements, nobody
    // seated yet. Everything that was decided months earlier, arriving at once.
    // Nothing is described here that is not in the frame.
    alt: "A black and white photograph of long wooden tables laid for dinner beneath trees at dusk, tapered candles burning down their length among glassware and low flower arrangements, with the chairs still empty",
    width: 2400,
    height: 1602,
  },

  // Absent until the guide is genuinely published. Adding one means adding the
  // other, and `dateModified` may not precede `datePublished`.
  // datePublished: "YYYY-MM-DD",
  // dateModified: "YYYY-MM-DD",

  intro: [
    `Most wedding plans begin in a spreadsheet. One tab, a few columns, a list of things that need doing, and for the first several weeks it is the most reassuring document either of you owns.`,
    `What happens next is fairly consistent. Tabs multiply. Columns are added and never filled in. The same number appears in two places and disagrees with itself. Somewhere around the fourth month the spreadsheet stops being where you look things up and becomes another thing to keep alive.`,
    `This guide is about what genuinely earns a place in it: the information you will need to find again, the decisions you have already made, and the work still waiting on somebody.`,
  ],

  sections: [
    {
      heading: "What Should a Wedding Planning Spreadsheet Track?",
      content: [
        `A wedding planning spreadsheet should hold what you would otherwise have to reconstruct: what still needs doing, who is coming, who you have hired, what it costs, what has been paid, what you have settled, and when everything falls due.`,
        `In practice that is eight working areas — tasks, guests, vendors, the budget, payments, a timeline, decisions and documents — and almost every template covers the first five. The last three are where most spreadsheets quietly give way, because they hold a plan together rather than list it.`,
        `The goal is not completeness. A tracker that follows everything is a tracker nobody updates, and a stale one is worse than a short one: it looks authoritative and is wrong.`,
      ],
    },
    {
      heading: "Information, Decisions and Actions",
      content: [
        `A planning tracker holds three different kinds of thing at once, and separating them is the quickest way to work out what any given column is for.`,
        { type: "subheading", text: "Information" },
        `Things you need to remember: a phone number, the venue address, what a contract covers, a guest's dietary requirement. It changes rarely, and its only job is to be findable.`,
        { type: "subheading", text: "Decisions" },
        `Things you have chosen: the photographer you booked, the menu you settled on, the table arrangement you agreed with the venue. A decision is finished rather than pending, but it still needs recording, because six weeks later somebody will ask what you decided.`,
        { type: "subheading", text: "Actions" },
        `Things somebody still has to do: send the final numbers, review the contract, confirm the delivery time. An action has an owner and a date, and it is the only one of the three that goes stale on its own.`,
        `Most spreadsheets carry information well, actions badly and decisions not at all, which is how a plan can look organized and still leave you unsure what is settled. It also makes a useful test for a new column: which of the three is it holding, and does that kind of thing already live somewhere?`,
        {
          type: "note",
          text: `This is a practical way to sort planning information, not an established methodology. Use it if it makes your own tracker easier to reason about, and ignore it if it does not.`,
        },
      ],
    },
    {
      heading: "Start With One Source of Truth",
      content: [
        `The commonest planning failure is not disorganization but duplication: the guest list in a spreadsheet and also in a notes app, a quote in an email and also typed into the budget, two copies of one file with slightly different names.`,
        `What follows is predictable:`,
        {
          type: "list",
          items: [
            `the same figure appears twice, and the two disagree`,
            `one copy is updated and the other is not`,
            `nobody is certain which version is current`,
            `a change falls between the copies and lands in neither`,
          ],
        },
        `The fix is not more discipline. It is giving every kind of information exactly one home and making everything else point at it, so that when something changes there is only one thing to change. A column belongs wherever the thing it describes already lives: a guest's meal choice sits on the guest list, and the catering area reads its total from there.`,
      ],
    },
    {
      heading: "Track Tasks, Not Just Categories",
      content: [
        { type: "subheading", text: "What makes a task a task" },
        `Most wedding checklists are lists of categories: flowers, music, transport, stationery. They are good for making sure nothing is forgotten and no use at all for making anything happen.`,
        `"Flowers" is not a task: nobody can start it, finish it, or tell you whether it is late. "Choose a florist and confirm the package" is a task, because it has a moment at which it is unambiguously done. That is the test — if two people can read a row and disagree about whether it is finished, it is a category wearing a verb, and it wants breaking into the two or three things that actually have to happen.`,
        { type: "subheading", text: "Give every task a deadline and an owner" },
        `A task with no date and no name attached does not move: a list of undated, unassigned work has no way of telling you which row deserves today. A workable row carries the task written as an action, one owner, a deadline, a status, and a note where there is something the title cannot hold.`,
        `Single ownership matters more than it sounds. Shared ownership reads as fair and behaves as nobody: two of the commonest reasons a wedding task is late are that each of you thought the other had it, and that neither wanted to ask. Keep the statuses short and keep them yours, too — three words, five, or a single checkbox all work, as long as each means the same thing every time it is used.`,
        `And a deadline is more useful as the date something should be started than the date it must be finished.`,
      ],
    },
    {
      heading: "Keep the Guest List in Its Own Working Area",
      content: [
        `The guest list changes most and touches most. Catering numbers, seating, stationery and several vendor quantities all read from it, which is why it should live in one place and be referenced rather than copied into each.`,
        `It wants a row per person, carrying:`,
        {
          type: "list",
          items: [
            `the name, as it should appear on an invitation`,
            `the household or invitation group they belong to`,
            `which side or circle they come from`,
            `contact details, in whatever form you will actually use`,
            `whether an invitation has been sent, and their reply`,
            `a companion or plus-one, where one applies`,
            `anything a vendor will need: a dietary requirement, an access need, a child's age`,
          ],
        },
        `The person is the unit, because a row reading "the Nikolaou family (4)" cannot be seated, catered for or counted reliably. The invitation group is a field of its own, so you can invite by household and count by head without keeping two lists in step.`,
        {
          type: "p",
          text: [
            `Building and grouping a list is a large subject in itself, and `,
            {
              text: "How to Organize a Wedding Guest List",
              href: "/guides/how-to-organize-wedding-guest-list",
            },
            ` covers it in full, replies included.`,
          ],
        },
      ],
    },
    {
      heading: "Track Vendors and What You Have Agreed",
      content: [
        `A vendor list holding names and phone numbers answers the easy question. The hard one, months later, is what you agreed to, and that is rarely written down anywhere except a contract nobody has reopened since signing.`,
        `A vendor row is worth carrying:`,
        {
          type: "list",
          items: [
            `the business, and the person you actually deal with`,
            `their contact details`,
            `what they are providing`,
            `where the conversation has got to: enquiry, quote, booked, agreement signed`,
            `whether a contract exists, and where the signed copy lives`,
            `the agreed total`,
            `the dates that matter: arrival, setup, delivery, how long they stay`,
            `what you are waiting on from them`,
          ],
        },
        `The status is the column that earns its keep. Two vendors quoting the same figure are in entirely different positions if one has countersigned and the other has not answered an email in three weeks.`,
        {
          type: "p",
          text: [
            `Holding a vendor, their agreement and their payment schedule together is a subject of its own: `,
            {
              text: "How to Keep Track of Wedding Vendors and Payments",
              href: "/guides/how-to-track-wedding-vendors-payments",
            },
            ` covers the whole record.`,
          ],
        },
      ],
    },
    {
      heading: "Track Payments Separately From the Budget",
      content: [
        `These two get merged into one tab constantly, and the merge is what makes the numbers untrustworthy. A budget is a plan; payments are events.`,
        `It is worth keeping five figures apart:`,
        {
          type: "list",
          items: [
            `estimated, meaning what you currently think something may cost`,
            `quoted, meaning what a vendor has actually said it will cost`,
            `committed, meaning what you have agreed to, paid or not`,
            `paid, meaning what has genuinely left your account`,
            `remaining, meaning committed minus paid, which is money you already owe`,
          ],
        },
        `The trap is subtracting paid from the budget and reading the result as what is left to spend. It is not: that treats everything you have committed to and not yet paid as money still available.`,
        `In tracker terms this is two areas rather than one. A budget line per category or item, carrying the estimated, quoted and committed figures; and a row per payment, carrying the vendor, a label, the amount, the due date and whether it has cleared.`,
        {
          type: "p",
          text: [
            `The money side has a guide of its own: `,
            {
              text: "How to Create a Wedding Budget",
              href: "/guides/how-to-create-wedding-budget",
            },
            ` covers setting the total, allocating it and reading what genuinely remains.`,
          ],
        },
      ],
    },
    {
      heading: "Track Decisions and Open Questions",
      content: [
        `This is the area almost no template includes. A wedding is a long sequence of choices, and a plan recording only tasks and costs loses both halves of it: what was decided, and what has not been decided yet.`,
        `Each causes its own trouble. An open question quietly blocks work: you cannot brief a florist until the colours are settled, and nobody notices the dependency until the florist asks. A settled question, unrecorded, gets reopened three months later, when one of you remembers agreeing to a buffet and the other remembers agreeing to think about it.`,
        `A decision row is short:`,
        {
          type: "list",
          items: [
            `the question, written as a question`,
            `whether it is open or settled`,
            `the answer, once there is one`,
            `the date it has to be answered by, where something is waiting on it`,
            `a line on why, because the reasoning is what disappears fastest`,
          ],
        },
        `That last one earns its column. Most reopened decisions are reopened not because the answer was wrong but because nobody remembers what it was weighed against, and one line — plated rather than buffet, because the room does not work for a queue — usually ends the conversation before it restarts.`,
        `Read from top to bottom, it also gives you something no task list can: an honest view of how much of the wedding is genuinely settled.`,
      ],
    },
    {
      heading: "Keep Documents and Supporting Information Findable",
      content: [
        `A spreadsheet makes a good index and a poor filing cabinet, and the distinction is worth holding on to: a surprising amount of planning time goes on looking for a document that certainly exists.`,
        `Things worth being able to find quickly:`,
        {
          type: "list",
          items: [
            `signed contracts, and any revised version`,
            `quotes, invoices and receipts`,
            `written confirmation of anything agreed by email or message`,
            `links: a floor plan, a shared folder, a supplier's portal`,
            `notes and photographs from meetings and site visits`,
          ],
        },
        `Rather than pasting a contract's contents into a cell, keep the files somewhere durable that you control and point the tracker at them: what the document is, who it belongs to, where it lives. The tracker tells you the agreement exists and is signed; the file is the agreement. And choose one location, because documents spread across two inboxes, a phone and a shared drive are, in practice, documents you do not have.`,
      ],
    },
    {
      heading: "Build a Planning Timeline",
      content: [
        `Tasks tell you what needs doing. A timeline tells you when the plan itself falls due, which is a different question and the one that tends to get answered late.`,
        `It is a short list of dated milestones rather than a second copy of the task list:`,
        {
          type: "list",
          items: [
            `the bookings that have to be in place before anything else can proceed`,
            `the date invitations go out, and the reply deadline`,
            `the dates each vendor needs final numbers by`,
            `final payment dates`,
            `the last week: confirmations, final counts, deliveries, anything to collect`,
            `the day itself, once its running order exists`,
          ],
        },
        `Most of these are not yours to choose. A vendor's cut-off for final numbers and a contract's final payment date are handed to you, and the useful work is getting them into one dated view early enough to plan backwards from. Two large payments landing in the same fortnight is something to discover in March.`,
        {
          type: "p",
          text: [
            `The wedding-day running order belongs here too, though it arrives late and is a document in its own right. For the order the rest of the work happens in, `,
            {
              text: "Wedding Planning Checklist: What to Organize and When",
              href: "/guides/wedding-planning-checklist",
            },
            ` walks the whole plan phase by phase.`,
          ],
        },
      ],
    },
    {
      heading: "What a Simple Wedding Planning Spreadsheet Looks Like",
      content: [
        `Put together, a tracker that holds a plan without becoming one is roughly eight areas, each with a handful of fields:`,
        {
          type: "list",
          items: [
            `Tasks, holding actions: the task, an owner, a deadline, a status, a note`,
            `Guests, holding people: the person, their household, contact details, their reply`,
            `Vendors, holding relationships: the business and contact, what they provide, where the conversation stands, whether an agreement is signed, the agreed total`,
            `Budget, holding the plan for the money: the category or item, estimated, quoted, committed`,
            `Payments, holding transactions: the vendor, a label, the amount, the due date, paid or not`,
            `Timeline, holding dated milestones: the date, what falls due, who is seeing to it`,
            `Decisions, holding choices: the question, open or settled, the answer, why`,
            `Documents, holding references: what it is, who it belongs to, where it lives`,
          ],
        },
        `That is a starting point rather than a specification, and not a set of tabs to create on the first afternoon: an empty tab is not neutral, it reads as work you have failed to do. A section earns its place when it solves a planning problem you actually have, and seating is the clearest example — for most of the planning it is not a real area at all, because you cannot seat people who have not replied. Begin with tasks, guests and money, and let the rest arrive when something forces it.`,
      ],
    },
    {
      heading: "Keeping It Simple, and Keeping It Current",
      content: [
        { type: "subheading", text: "Fewer columns than a template gives you" },
        `A theoretically complete spreadsheet nobody has opened since April is worse than a plain one that is current. Everything a tracker holds costs something to keep true, and that cost falls due every week.`,
        `Most of what makes one unmaintainable is avoidable:`,
        {
          type: "list",
          items: [
            `columns added because they looked thorough, and never filled in`,
            `the same fact typed into two places`,
            `tabs created in advance for work that has not started`,
            `status words detailed enough that you deliberate before picking one`,
            `formulas and formatting elaborate enough that changing a row feels risky`,
            `a structure only one of you understands`,
          ],
        },
        `Treat every empty column as a question: if it has been blank for a month, either it is not information you need or it lives somewhere else. And if recording a reply means editing three cells and re-checking a total, it will not happen on a Tuesday evening.`,
        { type: "subheading", text: "How often should you update it?" },
        `There is no correct interval, and a schedule is less useful than a short list of moments:`,
        {
          type: "list",
          items: [
            `after a decision is made, while you still remember the reasoning`,
            `after a vendor is booked, or an agreement is signed or changed`,
            `after a payment leaves your account`,
            `when a reply arrives, or a guest's details change`,
            `after any conversation in which something was agreed`,
          ],
        },
        `Between them, a short repeated pass keeps the numbers honest. Many couples find a weekly one comfortable once vendors start being booked, and monthly adequate before that; the right rhythm is the one you keep.`,
      ],
    },
    {
      heading: "Spreadsheet or Wedding Planning Tool?",
      content: [
        { type: "subheading", text: "When a spreadsheet starts getting in the way" },
        `A spreadsheet is an excellent planning tool, and there is one shape of problem it cannot solve. The symptoms are recognizable:`,
        {
          type: "list",
          items: [
            `the same guest, vendor or figure appears in more than one place`,
            `there are two versions of the file and neither is clearly current`,
            `changing one number means changing three others by hand`,
            `a payment date was missed because it sat in a tab nobody opens`,
            `statuses have drifted, and half the rows say something that stopped being true`,
          ],
        },
        `Every one of those is the same underlying thing: a spreadsheet stores values and does not know what they mean to one another. Twenty guests declining does not move the catering number, the seating area or the per-head figure in the budget. Doing that arithmetic by hand is entirely reasonable for a straightforward wedding, and stops being reasonable at about the point where you are copying one change into three places and are no longer sure you have caught them all.`,
        { type: "subheading", text: "A spreadsheet works well when" },
        {
          type: "list",
          items: [
            `you want to design the structure yourself, and change it freely`,
            `you are comfortable maintaining it, and will`,
            `the wedding is relatively straightforward`,
            `most of the work is arithmetic, which is what spreadsheets are best at`,
          ],
        },
        { type: "subheading", text: "A connected planning system helps more when" },
        {
          type: "list",
          items: [
            `several areas have to stay in step with each other`,
            `guest changes ripple into catering, seating and cost`,
            `more than one person is keeping information current`,
            `you find yourself copying the same fact between lists`,
            `tasks, vendors, costs and replies all need to be right at the same time`,
          ],
        },
        `Plenty of couples end up with both, which is a legitimate answer rather than a failure to choose: a spreadsheet for the modelling, a planning tool for the parts that have to stay connected. What does not work is the same information living in each.`,
      ],
    },
    {
      // The commission closes with a separate Agapaé section, which is what the
      // `related` block below already renders as an H2. Two H2s reading the
      // same words is a defect, and every earlier guide resolved it the same
      // way: this section is named for what it does, and "Where this fits in
      // Agapaé" stays with the related block.
      heading: "Keeping the Whole Plan in One Place",
      content: [
        {
          // The only plan-availability claim in the guide, in the same place
          // and the same block type every earlier guide put theirs.
          type: "note",
          text: `Agapaé's Planning Hub and Éros are part of the Heirloom plan.`,
        },
        `Agapaé's Planning Hub is the system described above, already assembled.`,
        `Planning is divided into the parts a wedding is made of, all editable, and each part holds its own vendors, tasks, costs, documents and open decisions. A vendor card carries the person you deal with, their details and your notes, with a status that follows the real conversation from researching through to booked. A task carries a due date, a priority, a checklist and your own labels, and is flagged once it is past its date. A cost carries a label, an amount, a due date and the vendor it belongs to, is due or paid, and rolls up into four totals: estimated cost, paid, outstanding, and what is left of your budget.`,
        `The two areas most spreadsheets leave out are here as well. Open questions are recorded as decisions, with the options under consideration, and keep their answer once settled. Contracts, quotes and receipts can be filed as PDFs or photographs against the vendor or task they belong to. Dated tasks, unpaid payments and meetings share a calendar, and the plan's milestones sit on a timeline.`,
        `Éros is the planning assistant inside that space. It reads what is there and answers from it, and when you ask it makes the change itself: add the task, record the payment, update the vendor. It suggests and you decide.`,
        `What it does not do is worth saying plainly. Agapaé does not import an existing spreadsheet and does not synchronize with Excel or Google Sheets. It is not a vendor directory, nobody is contacted, booked or paid through it, and it sends no reminders about vendor payments. It is not accounting software, and it is no substitute for your signed agreements.`,
        {
          type: "p",
          text: [
            `The planning side is described in full on `,
            { text: "Wedding Planning", href: "/wedding-planning" },
            `.`,
          ],
        },
      ],
    },
  ],

  faqHeading: "Frequently Asked Questions",

  // Visible copy, always expanded. `faqSchema` is deliberately not set, exactly
  // as on the thirteen earlier guides: Google restricts FAQ rich results to
  // government and health sites, so the markup would buy nothing while
  // committing us to a second copy of every answer.
  faq: [
    {
      q: `What should be included in a wedding planning spreadsheet?`,
      a: `At minimum: tasks with an owner and a deadline, the guest list, the vendors you are talking to, the budget, the payments that fall due, and a timeline of the dates the plan hangs on. Two more are worth adding and usually missing: the decisions you have made, so they are not reopened, and a note of where each contract and quote lives. Anything past that should be added because something is going wrong without it.`,
    },
    {
      q: `What tabs should a wedding planning spreadsheet have?`,
      a: `Fewer than most templates give you. Tasks, guests and money are the three you will use from the first week, and vendors follow as soon as you start talking to anyone. Timeline, decisions and documents are worth adding early because they are cheap to maintain. Seating, honeymoon, packing and the rest are better added at the point they become real work, since an empty tab reads as something you have failed to do.`,
    },
    {
      q: `Should I track wedding payments in the spreadsheet?`,
      a: `Yes, and separately from the budget. A budget is a plan for what things should cost; payments are dated events with a status. Give each payment its own row with the vendor, the amount, the due date and whether it has cleared, and keep the committed total apart from the paid total. Subtracting only what you have paid from your budget reads money you already owe as money you can still spend.`,
    },
    {
      q: `How often should I update my wedding planning spreadsheet?`,
      a: `Less a schedule than a set of moments: after a decision, after a booking, after a payment, when a reply arrives, and after any conversation in which something was agreed. Many couples add a short weekly pass once vendors start being booked, and monthly is usually enough before that. The interval matters far less than the habit of writing things down while the conversation is still happening.`,
    },
    {
      q: `Do I need a wedding planning spreadsheet?`,
      a: `You need one place where the plan lives; a spreadsheet is one good way to have it. It suits couples who want to shape the structure themselves and are content to maintain it, and it handles the arithmetic better than most alternatives. It struggles when several areas have to stay in step, when more than one person is updating, or when you are copying the same change into three places. Either way, the thing that matters is that there is one of it.`,
    },
  ],

  related: {
    heading: "Where this fits in Agapaé",

    // Two, as the house convention has it. The checklist owns the order of the
    // whole plan this article gives a shape to, and the vendor guide owns the
    // deepest of the areas it describes. Both are linked once in the prose at
    // the point the subject genuinely changes; naming them here as well is the
    // cluster relationship rather than link volume, and keeps each destination
    // at two occurrences.
    guides: ["wedding-planning-checklist", "how-to-track-wedding-vendors-payments"],

    product: [
      {
        href: "/wedding-planning",
        label: "Wedding Planning",
        blurb:
          "See how the Planning Hub keeps your tasks, vendors, costs, documents and open decisions together in one private space, with a calendar and a timeline over the top. Available on the Heirloom plan.",
      },
    ],
  },
};

export default weddingPlanningSpreadsheetWhatToTrackEn;
